import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Search,
  Filter,
  RotateCw,
  CheckCircle2,
  XCircle,
  Activity,
  Users,
  GitBranch,
  ChevronLeft,
  ChevronRight,
  Eye,
  Trash2,
  ChevronDown,
  X,
} from "lucide-react";
import { ActivityLogsService } from "../../../services/activityLogs.service";
import {
  getAuthSession,
  getAuthUser,
  getCurrentDashboard,
} from "../../AdminAuth/components/authStore";
import { appConfirm } from "../../../utils/appPopup";

const DEFAULT_FILTERS = {
  q: "",
  service: "",
  serviceGroup: "main_admin",
  action: "",
  actorType: "",
  targetType: "",
  entityType: "",
  warehouseId: "",
  success: "",
  from: "",
  to: "",
};

const PAGE_SIZE = 20;

const SERVICE_GROUP_OPTIONS = [
  { value: "main_admin", label: "Main Admin Panel" },
  { value: "warehouse", label: "Warehouse" },
  { value: "driver", label: "Driver" },
  { value: "user", label: "User" },
];

const STATIC_SERVICE_OPTIONS = [
  { value: "", label: "All" },
  { value: "auth", label: "Auth" },
  { value: "catalog", label: "Catalog" },
  { value: "catalog-service", label: "Catalog Service" },
  { value: "create-warehouse", label: "Create Warehouse" },
  { value: "warehouse", label: "Warehouse" },
  { value: "warehouse-inventory", label: "Warehouse Inventory" },
  { value: "delivery-partner-service", label: "Driver" },
  { value: "user-service", label: "User Service" },
  { value: "order-service", label: "Order Service" },
];

const STATIC_ACTOR_TYPE_OPTIONS = [
  { value: "", label: "All" },
  { value: "admin", label: "Admin" },
  { value: "super_admin", label: "Super Admin" },
  { value: "admin_employee", label: "Admin Employee" },
  { value: "warehouse_employee", label: "Warehouse Employee" },
  { value: "delivery_partner", label: "Delivery Partner" },
  { value: "user", label: "User" },
  { value: "customer", label: "Customer" },
];

const STATIC_TARGET_TYPE_OPTIONS = [
  { value: "", label: "All" },
  { value: "product", label: "Product" },
  { value: "category", label: "Category" },
  { value: "brand", label: "Brand" },
  { value: "unit", label: "Unit" },
  { value: "warehouse", label: "Warehouse" },
  { value: "warehouse_order", label: "Warehouse Order" },
  { value: "warehouse_employee", label: "Warehouse Employee" },
  { value: "delivery_partner", label: "Delivery Partner" },
  { value: "inventory", label: "Inventory" },
  { value: "inbound_receipt", label: "Inbound Receipt" },
  { value: "master_product", label: "Master Product" },
  { value: "rbac", label: "RBAC" },
  { value: "cart", label: "Cart" },
  { value: "order", label: "Order" },
  { value: "inventory_transfer", label: "Inventory Transfer" },
];

const STATIC_ENTITY_TYPE_OPTIONS = [
  { value: "", label: "All" },
  { value: "product", label: "Product" },
  { value: "category", label: "Category" },
  { value: "brand", label: "Brand" },
  { value: "unit", label: "Unit" },
  { value: "warehouse", label: "Warehouse" },
  { value: "warehouse_order", label: "Warehouse Order" },
  { value: "warehouse_employee", label: "Warehouse Employee" },
  { value: "delivery_partner", label: "Delivery Partner" },
  { value: "inventory", label: "Inventory" },
  { value: "inventory_stock", label: "Inventory Stock" },
  { value: "inventory_batch", label: "Inventory Batch" },
  { value: "inbound_receipt", label: "Inbound Receipt" },
  { value: "master_product", label: "Master Product" },
  { value: "rbac", label: "RBAC" },
  { value: "cart", label: "Cart" },
  { value: "cart_hold", label: "Cart Hold" },
  { value: "checkout_precheck", label: "Checkout Precheck" },
  { value: "order", label: "Order" },
  { value: "payment_verification", label: "Payment Verification" },
  { value: "inventory_transfer", label: "Inventory Transfer" },
];

function formatDateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

function formatLabel(value, fallback = "-") {
  const s = String(value || "").trim();
  if (!s) return fallback;
  return s
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .replaceAll(".", " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function uniqueOptions(values = []) {
  return [...new Set(values.filter(Boolean).map((v) => String(v).trim()).filter(Boolean))];
}

function mergeOptionSets(staticOptions = [], dynamicValues = []) {
  const seen = new Set();
  const result = [];

  staticOptions.forEach((item) => {
    if (!seen.has(item.value)) {
      seen.add(item.value);
      result.push(item);
    }
  });

  dynamicValues.forEach((value) => {
    if (!seen.has(value)) {
      seen.add(value);
      result.push({ value, label: formatLabel(value) });
    }
  });

  return result;
}

function getLogStatus(meta) {
  if (typeof meta?.success === "boolean") return meta.success;
  if (typeof meta?.ok === "boolean") return meta.ok;
  const status = Number(meta?.status);
  if (Number.isFinite(status)) return status >= 200 && status < 400;
  return true;
}

function getActorName(item) {
  return item?.actor_name || item?.actor_email || item?.actor_id || "-";
}

function extractEntityName(item) {
  const meta = item?.meta || {};
  const request = meta?.requestChanges || {};
  const response = meta?.responsePreview || {};

  const candidates = [
    meta?.entityLabel,
    item?.entity_label,
    item?.entity_name,
    item?.target_name,
    item?.target_email,
    response?.name,
    response?.title,
    response?.label,
    response?.product_name,
    response?.productName,
    response?.warehouse_name,
    response?.warehouseName,
    request?.name,
    request?.title,
    request?.label,
    request?.product_name,
    request?.productName,
    request?.warehouse_name,
    request?.warehouseName,
  ];

  const picked = candidates.find((value) => String(value || "").trim());
  return picked ? String(picked).trim() : "";
}

function safeJson(value) {
  try {
    return JSON.stringify(value || {}, null, 2);
  } catch {
    return "{}";
  }
}

function summaryLine(item) {
  const label = extractEntityName(item);
  const action = formatLabel(item?.action);
  const savedSummary = String(item?.meta?.summaryText || item?.summary_text || "").trim();

  if (savedSummary) return savedSummary;
  if (label) return `${action} on ${label}`;
  return action;
}

function isTechnicalKey(key) {
  return [
    "id",
    "_id",
    "entityId",
    "warehouseId",
    "warehouse_id",
    "createdAt",
    "updatedAt",
    "created_at",
    "updated_at",
  ].includes(String(key));
}

function humanizeChange(key, value) {
  const label = formatLabel(key);
  if (typeof value === "boolean") return `${label} was set to ${value ? "Yes" : "No"}`;
  if (Array.isArray(value)) return `${label} was updated`;
  if (value && typeof value === "object") return `${label} was updated`;
  return `${label} was changed to ${String(value)}`;
}

function changeLines(item) {
  const changes = item?.meta?.requestChanges;
  if (!changes || typeof changes !== "object") return [];
  return Object.entries(changes)
    .filter(([key, v]) => !isTechnicalKey(key) && v !== undefined && v !== null && v !== "")
    .map(([key, value]) => humanizeChange(key, value));
}

function getItemDisplayName(item) {
  const label = extractEntityName(item);
  if (label) return label;

  const action = String(item?.action || "").toUpperCase();
  if (action.includes("LIST_VIEW") || action.includes("PROFILE_VIEW") || action.includes("CONTEXT_SWITCHED")) {
    return "System activity";
  }

  return "Name was not saved in this old log";
}

function StatCard({ title, value, icon: Icon, tone = "default" }) {
  const toneClass = {
    default: "bg-white text-slate-700 border-[#f2c6cc]",
    success: "bg-emerald-50 text-emerald-700 border-emerald-200",
    danger: "bg-rose-50 text-rose-700 border-rose-200",
    accent: "bg-[#fff6f7] text-[#d86a78] border-[#f2c6cc]",
  }[tone];

  return (
    <div className={`rounded-[20px] border p-5 shadow-sm ${toneClass}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[13px] font-medium uppercase tracking-wide text-slate-400">{title}</div>
          <div className="mt-2 text-[28px] font-bold">{value}</div>
        </div>
        <div className="rounded-2xl bg-white/80 p-3 shadow-sm">
          <Icon size={20} />
        </div>
      </div>
    </div>
  );
}

function TinyBadge({ children, tone = "default" }) {
  const classes = {
    default: "bg-slate-100 text-slate-600 border-slate-200",
    success: "bg-emerald-50 text-emerald-700 border-emerald-200",
    danger: "bg-rose-50 text-rose-700 border-rose-200",
    accent: "bg-[#fff6f7] text-[#d86a78] border-[#f2c6cc]",
  }[tone];

  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${classes}`}>
      {children}
    </span>
  );
}

function FilterInput({ label, ...props }) {
  return (
    <label className="block">
      <div className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <input
        {...props}
        className="w-full rounded-[14px] border border-[#f2c6cc] bg-white px-4 py-3 text-sm text-slate-700 outline-none transition placeholder:text-slate-300 focus:border-[#d86a78]"
      />
    </label>
  );
}

function FilterSelect({ label, children, value, onChange }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const options = React.Children.toArray(children)
    .filter(Boolean)
    .map((child) => ({
      value: child?.props?.value ?? "",
      label: child?.props?.children ?? "",
    }));

  const selectedOption =
    options.find((option) => String(option.value) === String(value)) || options[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectOption = (optionValue) => {
    onChange?.({ target: { value: optionValue } });
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative block">
      <div className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-slate-400">
        {label}
      </div>

      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`flex w-full items-center justify-between rounded-[14px] border bg-white px-4 py-3 text-left text-sm outline-none transition ${
          open
            ? "border-[#d86a78] ring-2 ring-[#f9dfe4]"
            : "border-[#f2c6cc] hover:border-[#d86a78]"
        }`}
      >
        <span className="max-w-[220px] truncate text-slate-700">
          {selectedOption?.label || "All"}
        </span>
        <ChevronDown
          size={16}
          className={`shrink-0 text-slate-400 transition ${open ? "rotate-180 text-[#d86a78]" : ""}`}
        />
      </button>

      {open ? (
        <div className="absolute left-0 right-0 top-[74px] z-[80] max-h-[260px] overflow-y-auto rounded-[16px] border border-[#f2c6cc] bg-white p-1 shadow-[0_18px_45px_rgba(15,23,42,0.16)]">
          {options.map((option) => {
            const active = String(option.value) === String(value);

            return (
              <button
                key={`${label}-${option.value || "all"}`}
                type="button"
                onClick={() => selectOption(option.value)}
                className={`flex w-full items-center justify-between rounded-[12px] px-3 py-2.5 text-left text-sm transition ${
                  active
                    ? "bg-[#fff1f3] font-semibold text-[#d86a78]"
                    : "text-slate-700 hover:bg-[#fff7f8] hover:text-[#d86a78]"
                }`}
              >
                <span className="truncate">{option.label}</span>
                {active ? (
                  <span className="ml-3 h-2 w-2 rounded-full bg-[#d86a78]" />
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="rounded-2xl border border-[#f2c6cc] bg-[#fffafb] p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-1 break-words text-sm text-slate-700">{value || "-"}</div>
    </div>
  );
}

function GroupPill({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${active
          ? "border-[#d86a78] bg-[#d86a78] text-white"
          : "border-[#f2c6cc] bg-white text-slate-600 hover:bg-[#fff6f7]"
        }`}
    >
      {children}
    </button>
  );
}

function DetailsModal({ item, open, onClose, onDelete, isSuperAdmin, deleting }) {
  if (!open || !item) return null;
  const changes = changeLines(item);
  const statusOk = getLogStatus(item?.meta);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4">
      <div className="max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-[28px] border border-[#f2c6cc] bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-[#f5d5da] px-6 py-5">
          <div>
            <div className="text-[12px] font-semibold uppercase tracking-[0.2em] text-[#d86a78]">Log details</div>
            <h2 className="mt-2 text-[24px] font-bold text-slate-800">{summaryLine(item)}</h2>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <TinyBadge tone="accent">{formatLabel(item?.service)}</TinyBadge>
              <TinyBadge tone={statusOk ? "success" : "danger"}>{statusOk ? "Success" : "Failed"}</TinyBadge>
              <TinyBadge>{formatDateTime(item?.created_at)}</TinyBadge>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isSuperAdmin ? (
              <button
                type="button"
                disabled={deleting}
                onClick={() => onDelete(item)}
                className="inline-flex items-center gap-2 rounded-[12px] border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-700 transition hover:bg-rose-100 disabled:opacity-60"
              >
                <Trash2 size={16} /> Delete log
              </button>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              className="rounded-[12px] border border-[#f2c6cc] p-2 text-slate-500 transition hover:bg-[#fff6f7] hover:text-[#d86a78]"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="max-h-[calc(90vh-88px)] overflow-y-auto px-6 py-6">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            <DetailRow label="Who did this" value={getActorName(item)} />
            <DetailRow label="Actor type" value={formatLabel(item?.actor_type)} />
            <DetailRow label="What happened" value={summaryLine(item)} />
            <DetailRow label="Service" value={formatLabel(item?.service)} />
            <DetailRow label="Item name" value={getItemDisplayName(item)} />
            <DetailRow label="Item type" value={formatLabel(item?.target_type || item?.entity_type)} />
            {/* <DetailRow label="Item ID" value={item?.target_id || item?.entity_id} /> */}
            {/* <DetailRow label="Request ID" value={item?.req_id} /> */}
            {/* <DetailRow label="Warehouse ID" value={item?.warehouse_id} /> */}
            <DetailRow label="Date and time" value={formatDateTime(item?.created_at)} />
            <DetailRow label="Result" value={statusOk ? "Action completed successfully." : "Action failed."} />
            <DetailRow label="Summary" value={summaryLine(item)} />
          </div>

         <div className="mt-6">
  <div className="rounded-[24px] border border-[#f2c6cc] bg-white p-5">
    <div className="text-[16px] font-bold text-slate-800">What changed</div>

    {changes.length ? (
      <div className="mt-4 space-y-3 text-sm text-slate-700">
        {changes.map((line, idx) => (
          <div
            key={idx}
            className="rounded-2xl border border-[#f5d5da] bg-[#fffafb] px-4 py-3"
          >
            {line}
          </div>
        ))}
      </div>
    ) : (
      <div className="mt-4 rounded-2xl border border-dashed border-[#f2c6cc] bg-[#fffafb] px-4 py-6 text-sm text-slate-500">
        Detailed field changes are not available in this old log.
      </div>
    )}
  </div>
</div>
        </div>
      </div>
    </div>
  );
}

export default function ActivityLogsPage() {
  const user = getAuthUser() || {};
  const session = getAuthSession() || {};
  const currentDashboard = getCurrentDashboard();
  const isSuperAdmin = String(user?.role || user?.userType || "").toLowerCase() === "super_admin";

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0 });
  const [summary, setSummary] = useState({
    total: 0,
    success_count: 0,
    failed_count: 0,
    unique_actors: 0,
    unique_requests: 0,
    byService: [],
    byActorType: [],
    byAction: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const warehouseScope = session?.warehouseAccess || session?.dashboardAccess?.warehouseScope || {};
  const selectedWarehouseId = session?.selectedWarehouseId || warehouseScope?.assignedWarehouseId || "";

  useEffect(() => {
    if (currentDashboard === "warehouse" && selectedWarehouseId) {
      const next = {
        ...DEFAULT_FILTERS,
        serviceGroup: "warehouse",
        warehouseId: selectedWarehouseId,
      };
      setFilters(next);
      setAppliedFilters(next);
    }
  }, [currentDashboard, selectedWarehouseId]);

  const queryParams = useMemo(
    () => ({ ...appliedFilters, page, limit: PAGE_SIZE }),
    [appliedFilters, page]
  );

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [listRes, summaryRes] = await Promise.all([
        ActivityLogsService.list(queryParams),
        ActivityLogsService.summary(appliedFilters),
      ]);

      setItems(Array.isArray(listRes?.items) ? listRes.items : []);
      setPagination(
        listRes?.pagination || {
          page,
          limit: PAGE_SIZE,
          total: Array.isArray(listRes?.items) ? listRes.items.length : 0,
        }
      );
      setSummary(
        summaryRes?.data || {
          total: 0,
          success_count: 0,
          failed_count: 0,
          unique_actors: 0,
          unique_requests: 0,
          byService: [],
          byActorType: [],
          byAction: [],
        }
      );
    } catch (err) {
      setError(err?.message || "Failed to load activity logs.");
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, page, queryParams]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const totalPages = Math.max(
    1,
    Math.ceil(Number(pagination?.total || 0) / Number(pagination?.limit || PAGE_SIZE))
  );

  const serviceOptions = useMemo(() => {
    const dynamic = uniqueOptions([
      ...items.map((item) => item?.service),
      ...(summary?.byService || []).map((item) => item?.service),
    ]);
    return mergeOptionSets(STATIC_SERVICE_OPTIONS, dynamic);
  }, [items, summary]);

  const actionOptions = useMemo(() => {
    const dynamic = uniqueOptions([
      ...items.map((item) => item?.action),
      ...(summary?.byAction || []).map((item) => item?.action),
    ]);
    return mergeOptionSets([{ value: "", label: "All" }], dynamic);
  }, [items, summary]);

  const actorTypeOptions = useMemo(() => {
    const dynamic = uniqueOptions([
      ...items.map((item) => item?.actor_type),
      ...(summary?.byActorType || []).map((item) => item?.actor_type),
    ]);
    return mergeOptionSets(STATIC_ACTOR_TYPE_OPTIONS, dynamic);
  }, [items, summary]);

  const targetTypeOptions = useMemo(() => {
    const dynamic = uniqueOptions(items.map((item) => item?.target_type));
    return mergeOptionSets(STATIC_TARGET_TYPE_OPTIONS, dynamic);
  }, [items]);

  const entityTypeOptions = useMemo(() => {
    const dynamic = uniqueOptions(items.map((item) => item?.entity_type));
    return mergeOptionSets(STATIC_ENTITY_TYPE_OPTIONS, dynamic);
  }, [items]);

  const warehouseOptions = useMemo(() => {
    const dynamic = uniqueOptions([
      selectedWarehouseId,
      ...items.map((item) => item?.warehouse_id),
      ...items.map((item) => item?.meta?.warehouseId),
    ]);
    return mergeOptionSets([{ value: "", label: "All" }], dynamic);
  }, [items, selectedWarehouseId]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "service" && value) next.serviceGroup = "";
      return next;
    });
  };

  const applyFilters = (e) => {
    e?.preventDefault?.();
    setPage(1);
    setAppliedFilters(filters);
  };

  const clearFilters = () => {
    const next =
      currentDashboard === "warehouse" && selectedWarehouseId
        ? { ...DEFAULT_FILTERS, serviceGroup: "warehouse", warehouseId: selectedWarehouseId }
        : DEFAULT_FILTERS;
    setFilters(next);
    setAppliedFilters(next);
    setPage(1);
  };

  const applyServiceGroup = (serviceGroup) => {
    const next = {
      ...filters,
      serviceGroup,
      service: "",
    };
    setFilters(next);
    setAppliedFilters(next);
    setPage(1);
  };

  const openDetails = async (item) => {
    try {
      const res = await ActivityLogsService.getById(item.id);
      setDetailItem(res?.data || item);
      setDetailOpen(true);
    } catch (err) {
      setError(err?.message || "Unable to open log details.");
    }
  };

  const handleDeleteOne = async (item) => {
    if (!isSuperAdmin) return;
    const ok = await appConfirm("This log entry will be deleted permanently. Do you want to continue?", { danger: true, confirmText: "DELETE" });
    if (!ok) return;
    try {
      setDeleting(true);
      await ActivityLogsService.deleteById(item.id);
      setDetailOpen(false);
      setDetailItem(null);
      await loadData();
    } catch (err) {
      setError(err?.message || "Unable to delete log.");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteAll = async () => {
    if (!isSuperAdmin) return;
    const ok = await appConfirm("All visible logs for current filters will be deleted permanently. Do you want to continue?", { danger: true, confirmText: "DELETE" });
    if (!ok) return;
    try {
      setDeleting(true);
      await ActivityLogsService.deleteAll(appliedFilters);
      setDetailOpen(false);
      setDetailItem(null);
      await loadData();
    } catch (err) {
      setError(err?.message || "Unable to delete logs.");
    } finally {
      setDeleting(false);
    }
  };

  const topServices = Array.isArray(summary?.byService) ? summary.byService.slice(0, 4) : [];
  const topActions = Array.isArray(summary?.byAction) ? summary.byAction.slice(0, 4) : [];

  return (
    <div className="space-y-6">
      <div className="rounded-[24px] border border-[#f2c6cc] bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="text-[13px] font-semibold uppercase tracking-[0.2em] text-[#d86a78]">
              Activity Logs
            </div>
            <h1 className="warehouse-page-title mt-2">
              {isSuperAdmin ? "Master activity log" : "Your activity log"}
            </h1>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {isSuperAdmin ? (
              <button
                type="button"
                disabled={deleting}
                onClick={handleDeleteAll}
                className="inline-flex items-center justify-center gap-2 rounded-[14px] border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700 transition hover:bg-rose-100 disabled:opacity-60"
              >
                <Trash2 size={16} /> Delete visible logs
              </button>
            ) : null}
            <button
              type="button"
              onClick={loadData}
              className="inline-flex items-center justify-center gap-2 rounded-[14px] border border-[#f2c6cc] bg-white px-4 py-3 text-sm font-semibold text-[#d86a78] transition hover:bg-[#fff6f7]"
            >
              <RotateCw size={16} /> Refresh Logs
            </button>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {SERVICE_GROUP_OPTIONS.map((group) => (
            <GroupPill
              key={group.value}
              active={filters.serviceGroup === group.value}
              onClick={() => applyServiceGroup(group.value)}
            >
              {group.label}
            </GroupPill>
          ))}
        </div>
      </div>

      {error ? (
        <div className="rounded-[18px] border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <StatCard title="Total Logs" value={summary?.total || 0} icon={Activity} tone="accent" />
        <StatCard title="Success" value={summary?.success_count || 0} icon={CheckCircle2} tone="success" />
        <StatCard title="Failed" value={summary?.failed_count || 0} icon={XCircle} tone="danger" />
        <StatCard title="Unique Actors" value={summary?.unique_actors || 0} icon={Users} />
        <StatCard title="Requests" value={summary?.unique_requests || 0} icon={GitBranch} />
      </div>

      <form onSubmit={applyFilters} className="rounded-[24px] border border-[#f2c6cc] bg-white p-5 shadow-sm">
        <div className="mb-5 flex items-center gap-2 text-[16px] font-bold text-slate-700">
          <Filter size={18} className="text-[#d86a78]" /> Filters
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          <label className="block xl:col-span-2">
            <div className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-slate-400">
              Search
            </div>
            <div className="relative">
              <Search size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" />
              <input
                value={filters.q}
                onChange={(e) => handleFilterChange("q", e.target.value)}
                placeholder="Search by actor, product, request id, action..."
                className="w-full rounded-[14px] border border-[#f2c6cc] bg-white py-3 pl-11 pr-4 text-sm text-slate-700 outline-none transition placeholder:text-slate-300 focus:border-[#d86a78]"
              />
            </div>
          </label>

          <FilterSelect
            label="Service"
            value={filters.service}
            onChange={(e) => handleFilterChange("service", e.target.value)}
          >
            {serviceOptions.map((option) => (
              <option key={option.value || "all"} value={option.value}>
                {option.label}
              </option>
            ))}
          </FilterSelect>

          <FilterSelect
            label="Action"
            value={filters.action}
            onChange={(e) => handleFilterChange("action", e.target.value)}
          >
            {actionOptions.map((option) => (
              <option key={option.value || "all"} value={option.value}>
                {option.label}
              </option>
            ))}
          </FilterSelect>

          <FilterSelect
            label="Status"
            value={filters.success}
            onChange={(e) => handleFilterChange("success", e.target.value)}
          >
            <option value="">All</option>
            <option value="true">Success</option>
            <option value="false">Failed</option>
          </FilterSelect>

          <FilterSelect
            label="Actor Type"
            value={filters.actorType}
            onChange={(e) => handleFilterChange("actorType", e.target.value)}
          >
            {actorTypeOptions.map((option) => (
              <option key={option.value || "all"} value={option.value}>
                {option.label}
              </option>
            ))}
          </FilterSelect>

          <FilterSelect
            label="Target Type"
            value={filters.targetType}
            onChange={(e) => handleFilterChange("targetType", e.target.value)}
          >
            {targetTypeOptions.map((option) => (
              <option key={option.value || "all"} value={option.value}>
                {option.label}
              </option>
            ))}
          </FilterSelect>

          <FilterSelect
            label="Entity Type"
            value={filters.entityType}
            onChange={(e) => handleFilterChange("entityType", e.target.value)}
          >
            {entityTypeOptions.map((option) => (
              <option key={option.value || "all"} value={option.value}>
                {option.label}
              </option>
            ))}
          </FilterSelect>
          {/* 
          <FilterSelect
            label="Warehouse ID"
            value={filters.warehouseId}
            onChange={(e) => handleFilterChange("warehouseId", e.target.value)}
          >
            {warehouseOptions.map((option) => (
              <option key={option.value || "all"} value={option.value}>
                {option.label}
              </option>
            ))}
          </FilterSelect> */}

          <FilterInput
            label="From"
            type="datetime-local"
            value={filters.from}
            onChange={(e) => handleFilterChange("from", e.target.value)}
          />

          <FilterInput
            label="To"
            type="datetime-local"
            value={filters.to}
            onChange={(e) => handleFilterChange("to", e.target.value)}
          />
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button
            type="submit"
            className="rounded-[14px] bg-[#d86a78] px-5 py-3 text-sm font-semibold text-white transition hover:opacity-95"
          >
            Apply Filters
          </button>
          <button
            type="button"
            onClick={clearFilters}
            className="rounded-[14px] border border-[#f2c6cc] bg-white px-5 py-3 text-sm font-semibold text-slate-600 transition hover:bg-[#fff6f7]"
          >
            Reset
          </button>
        </div>
      </form>

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="rounded-[24px] border border-[#f2c6cc] bg-white p-5 shadow-sm">
          <div className="text-[16px] font-bold text-slate-800">Top services</div>
          <div className="mt-4 flex flex-wrap gap-2">
            {topServices.length ? (
              topServices.map((item) => (
                <TinyBadge key={item.service} tone="accent">
                  {formatLabel(item.service)} · {item.total}
                </TinyBadge>
              ))
            ) : (
              <div className="text-sm text-slate-400">No data yet.</div>
            )}
          </div>
        </div>

        <div className="rounded-[24px] border border-[#f2c6cc] bg-white p-5 shadow-sm">
          <div className="text-[16px] font-bold text-slate-800">Top actions</div>
          <div className="mt-4 flex flex-wrap gap-2">
            {topActions.length ? (
              topActions.map((item) => (
                <TinyBadge key={item.action}>
                  {formatLabel(item.action)} · {item.total}
                </TinyBadge>
              ))
            ) : (
              <div className="text-sm text-slate-400">No data yet.</div>
            )}
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-[24px] border border-[#f2c6cc] bg-white shadow-sm">
        <div className="flex flex-col gap-2 border-b border-[#f5d5da] px-5 py-5 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-[16px] font-bold text-slate-800">Log Entries</div>
            <div className="mt-1 text-sm text-slate-500">
              Showing {items.length} of {pagination?.total || 0} records
            </div>
          </div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">
            Sorted by latest activity first
          </div>
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">Loading logs...</div>
        ) : !items.length ? (
          <div className="p-10 text-center text-sm text-slate-500">No logs found for current filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left">
              <thead className="bg-[#fffafb] text-[12px] font-semibold uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="px-5 py-4">When</th>
                  <th className="px-5 py-4">Actor</th>
                  <th className="px-5 py-4">What happened</th>
                  <th className="px-5 py-4">Service</th>
                  <th className="px-5 py-4">Item</th>
                  {/* <th className="px-5 py-4">Request</th> */}
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const ok = getLogStatus(item?.meta);
                  const changes = changeLines(item);
                  return (
                    <tr key={item.id} className="border-t border-[#f9dde1] align-top">
                      <td className="px-5 py-5 text-sm text-slate-700">{formatDateTime(item.created_at)}</td>

                      <td className="px-5 py-5">
                        <div className="font-semibold text-slate-800">{getActorName(item)}</div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          <TinyBadge>{formatLabel(item.actor_type)}</TinyBadge>
                          {item.actor_email ? <TinyBadge>{item.actor_email}</TinyBadge> : null}
                        </div>
                      </td>

                      <td className="px-5 py-5">
                        <div className="font-semibold text-slate-800">{summaryLine(item)}</div>
                        {changes.length ? (
                          <div className="mt-2 text-sm text-slate-500">{changes.slice(0, 2).join(" • ")}</div>
                        ) : (
                          <div className="mt-2 text-sm text-slate-400">Tap view details for the full snapshot.</div>
                        )}
                      </td>

                      <td className="px-5 py-5">
                        <TinyBadge tone="accent">{formatLabel(item.service)}</TinyBadge>
                      </td>

                      <td className="px-5 py-5 text-sm text-slate-700">
                        <div className="font-semibold text-slate-800">{getItemDisplayName(item)}</div>
                        <div className="mt-1 text-slate-500">{formatLabel(item.entity_type || item.target_type)}</div>

                      </td>

                      {/* <td className="px-5 py-5 text-sm text-slate-500">{item.req_id || "-"}</td> */}

                      <td className="px-5 py-5">
                        {ok ? <TinyBadge tone="success">Success</TinyBadge> : <TinyBadge tone="danger">Failed</TinyBadge>}
                      </td>

                      <td className="px-5 py-5">
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => openDetails(item)}
                            className="inline-flex items-center gap-2 rounded-[12px] border border-[#f2c6cc] bg-white px-3 py-2 text-sm font-semibold text-[#d86a78] transition hover:bg-[#fff6f7]"
                          >
                            <Eye size={15} /> View
                          </button>
                          {isSuperAdmin ? (
                            <button
                              type="button"
                              disabled={deleting}
                              onClick={() => handleDeleteOne(item)}
                              className="inline-flex items-center gap-2 rounded-[12px] border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700 transition hover:bg-rose-100 disabled:opacity-60"
                            >
                              <Trash2 size={15} /> Delete
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-[#f5d5da] px-5 py-4 text-sm text-slate-500">
          <div>
            Page {pagination?.page || 1} of {totalPages}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="inline-flex items-center gap-1 rounded-[12px] border border-[#f2c6cc] bg-white px-3 py-2 font-semibold text-slate-600 transition hover:bg-[#fff6f7] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronLeft size={16} /> Prev
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="inline-flex items-center gap-1 rounded-[12px] border border-[#f2c6cc] bg-white px-3 py-2 font-semibold text-slate-600 transition hover:bg-[#fff6f7] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      <DetailsModal
        item={detailItem}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        onDelete={handleDeleteOne}
        isSuperAdmin={isSuperAdmin}
        deleting={deleting}
      />
    </div>
  );
}
